import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createAppTheme } from "../theme";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "share-moments-theme";

interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme(): void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function initialMode(): ThemeMode {
  const preset = document.documentElement.dataset.theme;
  if (preset === "light" || preset === "dark") return preset;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(initialMode);
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    try { window.localStorage.setItem(STORAGE_KEY, mode); } catch { /* Theme still applies when storage is unavailable. */ }
  }, [mode]);

  const value = useMemo<ThemeContextValue>(() => ({
    mode,
    toggleTheme: () => setMode((current) => current === "dark" ? "light" : "dark")
  }), [mode]);

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useThemeMode must be used inside AppThemeProvider");
  return context;
}
